import type { Visit } from "@/components/custom/appointment-modal/visit-history-section"
import { apiClient } from "@/lib/apiClient"
import { DateTime } from "luxon";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { ensureUtcIso } from "@/lib/time";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import { toItem } from "@/components/custom/attachments/mapItem";

// Backend attendance (app/domain/attendance.py) → UI status slug. It used to show a
// cancelled visit as "confirmed" and a waiting one as "no-show" (clients README §5.2 #23).
export function mapAttendanceToStatus(attendance: number | null | undefined): Visit["status"] {
  if (attendance === 1) return "visited"
  if (attendance === -1) return "no_show"
  if (attendance === 2) return "cancelled"
  return "waiting"   // 0 or never marked
}

function normalizeVisit(record: any): Visit {
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  // Normalize to UTC (handles strings with or without offsets),
  // then render date/time in salon TZ
  let datePart = "";
  let timePart = "";
  const src = record.date || record.datetime || record.created_datetime;
  if (src) {
    try {
      const isoUtc = ensureUtcIso(String(src), tz);
      const dSalon = DateTime.fromISO(isoUtc, { zone: "utc" }).setZone(tz);
      datePart = dSalon.toISODate();
      timePart = dSalon.toFormat("HH:mm");
    } catch {
      // keep blanks on parse errors
    }
  }
  return {
    id: record.id,
    date: datePart,
    time: timePart,
    employee: {
      name: record.staff?.name || "",
      photo: resolveMediaUrl(record.staff?.avatar) || undefined,
    },
    status: mapAttendanceToStatus(record.attendance),
    services: (record.services || []).map((s: any) => ({
      id: s.id,
      name: s.title,
      cost: s.cost_to_pay,
      paid: s.paid_sum ?? 0,
    })),
    additionalItems: record.services?.[0]?.consumables?.length
      ? record.services[0].consumables.map((c: any) => ({
        name: c.title,
        quantity: Math.abs(c.amount),
        unit: c.unit,
        cost: c.cost_per_unit ? Math.abs(c.amount) * c.cost_per_unit : 0,
      }))
      : undefined,
    comment: record.comment || undefined,
    location: record.company?.address || "",
    // the appointment's photos and videos (boona docs/codebase/attachments/), staff with records_access only
    attachments: Array.isArray(record.attachments) ? record.attachments.map(toItem) : [],
  }
}

/**
 * Fetches and normalizes visit history for a client in a company.
 * @param companyId
 * @param clientId
 * @returns Promise<Visit[]>
 */
export async function fetchVisitHistory(companyId: number, clientId: number): Promise<Visit[]> {
  const url = `/api/v1/company/client/${clientId}/visits/search`
  const response = await apiClient.get(url)
  // Debug: log the full response object
  // eslint-disable-next-line no-console
  // Always extract records from response.data.data.records
  const records = response?.data?.data?.records || []
  return records.map(normalizeVisit)
}
